/**
 * Z-index scale for the Alamo design system.
 *
 * Gaps of 100 between tiers so intermediates can fit in without a full reshuffle.
 *
 * Rules:
 * - Global overlays (portal-rendered) MUST pick a tier from this scale.
 * - Portal-rendered floating UI (Select/DropdownMenu/Popover/Tooltip and the
 *   components built on them) default to `FLOATING` (500) so they clear a modal
 *   (400) without any per-call-site config. Do not pass a z-index prop to "go
 *   over a modal" — that is now the default behaviour.
 * - Local stacking contexts (e.g. a z-10 sticky inside a scrollable table)
 *   remain at z-10 since their stacking is scoped to an ancestor, not the viewport.
 * - Never hardcode z-index in portal-rendered components outside @alamo/ui.
 */
export const Z = {
  /** Default (no stacking) */
  BASE: 0,
  /** Local stacking: sticky table headers, inline badges. Scoped to parent. */
  STICKY: 10,
  /** App sidebar (fixed). Must stay above base content but below floating UI. */
  SIDEBAR: 200,
  /**
   * Reserved tier for non-portal floating chrome that must stay below modals
   * (e.g. SidebarFlyout, which renders inside the sidebar stacking context).
   * Portal-rendered overlays (Select/DropdownMenu/Popover/Tooltip) do NOT use
   * this tier — they default to FLOATING so they clear a modal automatically.
   */
  DROPDOWN: 300,
  /**
   * Blocking overlays: Dialog, Sheet, ConfirmDialog, AlertDialog.
   * Above the sidebar and the reserved DROPDOWN tier.
   */
  MODAL: 400,
  /**
   * Default tier for ALL portal-rendered floating UI (Select, DropdownMenu,
   * Popover, Tooltip and everything built on them — DatePicker, MonthYearPicker,
   * Combobox, etc.). Sits above MODAL (400) so a dropdown opened from inside a
   * Dialog/Sheet renders in front of the modal automatically, with zero config
   * at the call site. Outside a modal nothing competes between 300 and 500, so
   * the same tier is correct in both contexts. Below TOAST.
   */
  FLOATING: 500,
  /**
   * @deprecated Alias of `FLOATING`. Portal-rendered overlays now default to
   * `FLOATING`, so opting a dropdown "over the modal" is no longer needed.
   * Kept for source compatibility with existing call sites; prefer omitting the
   * z-index prop entirely.
   */
  DROPDOWN_OVER_MODAL: 500,
  /** Toast notifications — always on top. */
  TOAST: 600,
} as const;

export type ZIndex = (typeof Z)[keyof typeof Z];
